import { ConfigError, type ConfigResult } from '@atlaskit/web-config-client';

import { DynamicConfigClient } from '../dynamic-config-client/dynamic-config-client';
import { type DynamicConfig, type DynamicConfigType } from '../dynamic-config-manifest';

export type DynamicConfigValueType = {
	boolean: boolean;
	number: number;
	string: string;
	stringList: string[];
	numberList: number[];
};

export type GetDynamicConfigOptions<T extends DynamicConfigType> = {
	config: DynamicConfig & { type: T };
	/**
	 * Default value returned when the config is missing, has the wrong type,
	 * or the client is not initialized.
	 */
	defaultValue: DynamicConfigValueType[T];
};

const isValueResult = <T>(result: ConfigResult<T>): result is { value: T } => {
	return !('error' in result);
};

/**
 * Returns the typed value for a dynamic configuration.
 *
 * Always returns `defaultValue` if:
 *   - the underlying `DynamicConfigClient` has not been initialized
 *   - the config key is missing in the FFS response
 *   - the config value has the wrong type
 *   - any unexpected error occurs while reading the config
 */
export function getDynamicConfig<T extends DynamicConfigType>(
	options: GetDynamicConfigOptions<T>,
): DynamicConfigValueType[T] {
	const { config, defaultValue } = options;
	const client = DynamicConfigClient.getInstance();

	if (!client.isInitialized()) {
		return defaultValue;
	}

	try {
		const collection = client.getCollection();
		let result: ConfigResult<DynamicConfigValueType[T]>;

		switch (config.type) {
			case 'boolean':
				result = collection.getBoolean(config.configKey) as ConfigResult<DynamicConfigValueType[T]>;
				break;
			case 'number':
				result = collection.getNumber(config.configKey) as ConfigResult<DynamicConfigValueType[T]>;
				break;
			case 'string':
				result = collection.getString(config.configKey) as ConfigResult<DynamicConfigValueType[T]>;
				break;
			case 'stringList':
				result = collection.getStringList(config.configKey) as ConfigResult<
					DynamicConfigValueType[T]
				>;
				break;
			case 'numberList':
				result = collection.getNumberList(config.configKey) as ConfigResult<
					DynamicConfigValueType[T]
				>;
				break;
			default: {
				const exhaustive: never = config.type;
				throw new Error(`Unsupported dynamic config type: ${String(exhaustive)}`);
			}
		}

		if (isValueResult(result)) {
			return result.value;
		}

		if (result.error !== ConfigError.NotFound) {
			console.warn(`getDynamicConfig: unexpected error for "${config.configKey}"`, result);
		}
		return defaultValue;
	} catch (error) {
		console.error(`getDynamicConfig: failed to read "${config.configKey}"`, error);
		return defaultValue;
	}
}
